/**
 * Custom API Error Classes
 *
 * Domain-specific errors for handling API responses
 * These errors are thrown by the service layer and caught by UI components
 */

/**
 * Base class for API errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Thrown when trying to claim a task that's already assigned to someone
 */
export class TaskAlreadyAssignedError extends ApiError {
  constructor(public readonly assignedTo: string) {
    super(`Task already assigned to ${assignedTo}`, 'TASK_ALREADY_ASSIGNED', 409);
    this.name = 'TaskAlreadyAssignedError';
  }
}

/**
 * Thrown when trying to claim a request that's already assigned to someone
 */
export class RequestAlreadyAssignedError extends ApiError {
  constructor(public readonly assignedTo: string) {
    super(`Request already assigned to ${assignedTo}`, 'REQUEST_ALREADY_ASSIGNED', 409);
    this.name = 'RequestAlreadyAssignedError';
  }
}

/**
 * Thrown when a resource is not found
 */
export class NotFoundError extends ApiError {
  constructor(resource: string, id?: string) {
    super(id ? `${resource} with ID ${id} not found` : `${resource} not found`, 'NOT_FOUND', 404);
    this.name = 'NotFoundError';
  }
}

/**
 * Thrown when user doesn't have permission for an action
 */
export class ForbiddenError extends ApiError {
  constructor(message = 'You do not have permission to perform this action') {
    super(message, 'FORBIDDEN', 403);
    this.name = 'ForbiddenError';
  }
}

/**
 * Type guard to check if error is an API error with status
 */
export function isApiErrorWithStatus(error: unknown): error is { status?: number; details?: Record<string, unknown> } {
  return typeof error === 'object' && error !== null && 'status' in error;
}

// ===========================
// AUTHENTICATION ERRORS
// ===========================

/**
 * Thrown when user tries to login but email is not verified
 */
export class EmailNotVerifiedError extends ApiError {
  constructor() {
    super('Email not verified', 'EMAIL_NOT_VERIFIED', 403);
    this.name = 'EmailNotVerifiedError';
  }
}

/**
 * Thrown when trying to register with an email that already exists
 */
export class AccountAlreadyExistsError extends ApiError {
  constructor() {
    super('Account already exists with this email', 'ACCOUNT_ALREADY_EXISTS', 409);
    this.name = 'AccountAlreadyExistsError';
  }
}

/**
 * Thrown when rate limit is exceeded (too many requests)
 */
export class TooManyRequestsError extends ApiError {
  constructor() {
    super('Too many requests, please try again later', 'TOO_MANY_REQUESTS', 429);
    this.name = 'TooManyRequestsError';
  }
}

/**
 * Thrown when password reset token is invalid
 */
export class InvalidResetTokenError extends ApiError {
  constructor() {
    super('Invalid or malformed reset token', 'INVALID_RESET_TOKEN', 400);
    this.name = 'InvalidResetTokenError';
  }
}

/**
 * Thrown when password reset token has expired
 */
export class ResetTokenExpiredError extends ApiError {
  constructor() {
    super('Reset token has expired', 'RESET_TOKEN_EXPIRED', 410);
    this.name = 'ResetTokenExpiredError';
  }
}
